import React, { useContext } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2, X } from 'lucide-react';
import clip from '../../../../assets/production-flow/rnc/clip.svg';
import {
  AttachmentListActions,
  AttachmentListContainer,
  AttachmentListIconButton,
  AttachmentListLinkButton,
  AttachmentListName,
  AttachmentListRow,
} from '../../../../components/AttachmentManager/style';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialEntryApi } from '../api';
import { FinancialEntryFormSchemaType } from './schema';
import { getItemValidationMessages } from './formValidation';
import FormCurrencyField from './FormCurrencyField';
import {
  AddItemButton,
  AttachmentClipLabel,
  AttachmentCount,
  AttachmentHeaderBar,
  CardTitle,
  CompetenceFieldsRow,
  CompetenceGroup,
  FormGrid,
  ItemCard,
  StyledCard,
  StyledFormGroup,
  SectionErrorMessage,
} from './style';

const ItemsSection: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinancialEntryFormSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const items = watch('items');
  const itemValidationMessages = getItemValidationMessages(items, errors.items);

  const handleFilePick = (itemIndex: number, fileList: FileList | null) => {
    if (!fileList?.length) return;
    const current = items?.[itemIndex]?.pendingFiles ?? [];
    const newPending = Array.from(fileList).map((file) => ({
      file,
      previewName: file.name,
    }));
    setValue(`items.${itemIndex}.pendingFiles`, [...current, ...newPending]);
  };

  const removePendingFile = (itemIndex: number, fileIndex: number) => {
    const current = items?.[itemIndex]?.pendingFiles ?? [];
    setValue(
      `items.${itemIndex}.pendingFiles`,
      current.filter((_, i) => i !== fileIndex),
    );
  };

  const openSignedUrl = async (
    itemId: number | undefined,
    fileId: number,
    options?: { downloadName?: string },
  ) => {
    if (!itemId) return;
    const url = options?.downloadName
      ? await financialEntryApi.downloadFile(itemId, fileId)
      : await financialEntryApi.viewFile(itemId, fileId);
    if (options?.downloadName) {
      const link = document.createElement('a');
      link.href = url;
      link.download = options.downloadName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleView = async (itemId: number | undefined, fileId: number) => {
    try {
      await openSignedUrl(itemId, fileId);
    } catch {
      setFeedback({
        message: 'Erro ao visualizar arquivo.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  const handleDownload = async (
    itemId: number | undefined,
    fileId: number,
    name: string,
  ) => {
    try {
      await openSignedUrl(itemId, fileId, { downloadName: name });
    } catch {
      setFeedback({
        message: 'Erro ao baixar arquivo.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  const handleViewPending = (file: File) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleDownloadPending = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteFile = async (
    itemIndex: number,
    itemId: number | undefined,
    fileId: number,
  ) => {
    if (!itemId) return;
    try {
      await financialEntryApi.deleteFile(itemId, fileId);
      const attachments = items?.[itemIndex]?.attachments ?? [];
      setValue(
        `items.${itemIndex}.attachments`,
        attachments.filter((a) => a.id !== fileId),
      );
      setFeedback({
        message: 'Arquivo removido.',
        isOpen: true,
        statusOk: true,
      });
    } catch {
      setFeedback({
        message: 'Erro ao remover arquivo.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  return (
    <StyledCard>
      <CardTitle>Itens</CardTitle>

      {fields.map((field, index) => {
        const itemId = items?.[index]?.id;
        const attachments = items?.[index]?.attachments ?? [];
        const pendingFiles = items?.[index]?.pendingFiles ?? [];
        const totalFiles = attachments.length + pendingFiles.length;
        const fileInputId = `financial-item-attach-${field.id}`;
        const itemError = errors.items?.[index];
        const itemValidationMessage = itemValidationMessages.find((message) =>
          message.startsWith(`Item ${index + 1}:`),
        );

        return (
          <ItemCard key={field.id}>
            <FormGrid cols={2}>
              <StyledFormGroup>
                <label>BU</label>
                <Controller
                  name={`items.${index}.businessUnit`}
                  control={control}
                  render={({ field: f }) => (
                    <input
                      {...f}
                      data-field-error={itemError?.businessUnit ? 'true' : undefined}
                    />
                  )}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label>Projeto</label>
                <Controller
                  name={`items.${index}.project`}
                  control={control}
                  render={({ field: f }) => (
                    <input
                      {...f}
                      data-field-error={itemError?.project ? 'true' : undefined}
                    />
                  )}
                />
              </StyledFormGroup>
            </FormGrid>

            <CompetenceGroup>
              <label>Competência</label>
              <CompetenceFieldsRow>
                <Controller
                  name={`items.${index}.competenceValue`}
                  control={control}
                  render={({ field: f }) => (
                    <FormCurrencyField
                      id={`item-competence-value-${index}`}
                      name={f.name}
                      value={f.value ?? ''}
                      onChange={f.onChange}
                      hasError={!!itemError?.competenceValue}
                    />
                  )}
                />
                <Controller
                  name={`items.${index}.competenceDate`}
                  control={control}
                  render={({ field: f }) => (
                    <input
                      {...f}
                      type="date"
                      data-field-error={itemError?.competenceDate ? 'true' : undefined}
                    />
                  )}
                />
              </CompetenceFieldsRow>
            </CompetenceGroup>

            <StyledFormGroup>
              <label>Anexo</label>
              <AttachmentHeaderBar>
                <AttachmentCount>
                  {totalFiles} arquivo(s)
                </AttachmentCount>
                <AttachmentClipLabel htmlFor={fileInputId}>
                  <img src={clip} alt="Anexar arquivo" width={16} height={16} />
                  <input
                      id={fileInputId}
                      type="file"
                      multiple
                      onChange={(e) => {
                        handleFilePick(index, e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </AttachmentClipLabel>
              </AttachmentHeaderBar>

              {totalFiles > 0 && (
                <AttachmentListContainer>
                  {attachments.map((file) => {
                    const fileName =
                      file.originalName || file.fileName || file.title || 'arquivo';

                    return (
                      <AttachmentListRow key={file.id}>
                        <AttachmentListName title={fileName}>
                          {fileName}
                        </AttachmentListName>
                        <AttachmentListActions>
                          <AttachmentListLinkButton
                            type="button"
                            onClick={() => handleView(itemId, file.id)}
                          >
                            Visualizar
                          </AttachmentListLinkButton>
                          <AttachmentListLinkButton
                            type="button"
                            onClick={() =>
                              handleDownload(itemId, file.id, fileName)
                            }
                          >
                            Download
                          </AttachmentListLinkButton>
                          <AttachmentListIconButton
                            type="button"
                            onClick={() =>
                              handleDeleteFile(index, itemId, file.id)
                            }
                            title="Remover"
                          >
                            <X size={14} />
                          </AttachmentListIconButton>
                        </AttachmentListActions>
                      </AttachmentListRow>
                    );
                  })}

                  {pendingFiles.map((pf, pfIndex) => (
                    <AttachmentListRow key={`pending-${pfIndex}`}>
                      <AttachmentListName title={pf.previewName}>
                        {pf.previewName}
                      </AttachmentListName>
                      <AttachmentListActions>
                        <AttachmentListLinkButton
                          type="button"
                          onClick={() => handleViewPending(pf.file)}
                        >
                          Visualizar
                        </AttachmentListLinkButton>
                        <AttachmentListLinkButton
                          type="button"
                          onClick={() => handleDownloadPending(pf.file)}
                        >
                          Download
                        </AttachmentListLinkButton>
                        <AttachmentListIconButton
                          type="button"
                          onClick={() => removePendingFile(index, pfIndex)}
                          title="Remover"
                        >
                          <X size={14} />
                        </AttachmentListIconButton>
                      </AttachmentListActions>
                    </AttachmentListRow>
                  ))}
                </AttachmentListContainer>
              )}
            </StyledFormGroup>

            {fields.length > 1 && (
              <AddItemButton type="button" onClick={() => remove(index)}>
                <Trash2 size={14} />
                Remover item
              </AddItemButton>
            )}

            {itemValidationMessage && (
              <SectionErrorMessage>{itemValidationMessage}</SectionErrorMessage>
            )}
          </ItemCard>
        );
      })}

      <AddItemButton
          type="button"
          onClick={() =>
            append({
              businessUnit: '',
              project: '',
              competenceValue: '',
              competenceDate: '',
              attachments: [],
              pendingFiles: [],
            })
          }
        >
          <Plus size={16} />
          Adicionar item
      </AddItemButton>
    </StyledCard>
  );
};

export default ItemsSection;
